import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Switch,
  Avatar,
  MenuItem,
  TextField,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import CapitalizeFirstLetter from "../../../shared/utils/CapitalizeFirstLetter.jsx";
import Decrypt from "../../../shared/utils/Decrypt.js";
import VendorList from "../PoIssuedComponent/VendorList.jsx";
import GlobalButton from "../../../shared/utils/GlobalButton.jsx";

const IPAssignModal = ({
  open,
  onClose,
  campaignId,
  initialSelected = [],
  getCampaignList,
  vendorId,
  getVendorList,
  vpList,
  getInstallationPartners,
  ipId,
}) => {
  const [ipList, setIpList] = useState([]);
  const [selectedIps, setSelectedIps] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectedVp, setSelectedVp] = useState(null);

  const token = localStorage.getItem("token");
  const serverURL = process.env.REACT_APP_URL;

  /* ---------------- SET INITIAL SELECTED IPs ---------------- */

  useEffect(() => {
    if (Array.isArray(initialSelected)) {
      let ids = initialSelected.map((ip) => ip?._id) || [];

      if (vpList) {
        setSelectedVp(initialSelected[0]._id);
      }

      setSelectedIps(ids); // current toggle state
    } else {
      setSelectedIps([]);
    }
  }, [initialSelected]);

  const getVPList = async () => {
    try {
      setLoadingList(true);
      const response = await axios.get(`${serverURL}/api/vendorsForMap`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          adminApproval: 1,
        },
      });

      if (response?.data?.success) {
        let decreryptedVPs = response?.data.data.map((ele) => {
          return {
            address: ele.address,
            city: ele.city,
            phoneNumber: Decrypt(ele.phoneNumber),
            pinCode: ele?.pinCode,
            id: ele.id,
            vendorBusinessName: ele.vendorBusinessName,
            profilePhoto: ele.profilePhoto,
          };
        });
        setIpList(decreryptedVPs || []);
      }
    } catch (error) {
      console.error("Error fetching Vendor Partners:", error);
    } finally {
      setLoadingList(false);
    }
  };

  /* ---------------- FETCH IP LIST (ONLY WHEN MODAL OPENS) ---------------- */
  useEffect(() => {
    if (vpList) {
      getVPList();
    } else {
      getInstallationPartnerList();
    }
  }, [open]);

  const getInstallationPartnerList = async () => {
    try {
      setLoadingList(true);
      const response = await axios.get(
        `${serverURL}/installationPartner/viewAllIpsMap`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response?.data?.success) {
        setIpList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching Installation Partners:", error);
    } finally {
      setLoadingList(false);
    }
  };

  /* ---------------- FILTERED LIST ---------------- */
  const filteredIp = useMemo(() => {
    return ipList.filter((ip) => {
      console.log("vp", ip);
      const searchValue = search.toLowerCase();

      return (
        ip.name?.toLowerCase().includes(searchValue) ||
        ip.phoneNumber?.toLowerCase().includes(searchValue) ||
        ip.vendorBusinessName?.toLowerCase().includes(searchValue)
      );
    });
  }, [ipList, search]);
  /* ---------------- TOGGLE SELECT (MULTIPLE) ---------------- */
  const handleToggle = (ipId) => {
    setSelectedIps((prev) =>
      prev.includes(ipId) ? prev.filter((id) => id !== ipId) : [...prev, ipId],
    );
  };

  const handleVPList = (id) => {
    setSelectedVp((prev) => (prev === id ? null : id));
  };

  /* ---------------- ASSIGN API ---------------- */
  const handleAssign = async () => {
    try {
      setAssigning(true);

      const payload = selectedIps.map((id) => ({
        ipId: id,
        isAssign: true,
      }));

      const response = await axios.post(
        `${serverURL}/jobs/assignCampaignToInstallationPartner`,
        {
          campaignId,
          ipsData: payload,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      await Swal.fire({
        customClass: {
          container: "my-swal",
        },
        icon: "success",
        title: "Success",
        text:
          response.data.message ||
          "Installation Partners assigned successfully",
        confirmButtonColor: "#1976d2",
        allowOutsideClick: false,
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) swalContainer.style.zIndex = 20000;
        },
      });

      getCampaignList();
      onClose();
    } catch (error) {
      Swal.fire({
        customClass: {
          container: "my-swal",
        },
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.error ||
          "Something went wrong. Please try again.",
        confirmButtonColor: "#d32f2f",
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) swalContainer.style.zIndex = 20000;
        },
      });
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignVP = async () => {
    try {
      setAssigning(true);

      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${serverURL}/installationPartner/assignVendors`,
        {
          installationPartnerId: ipId,

          vendorId: selectedVp,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.data.success) {
        await Swal.fire({
          customClass: {
            container: "my-swal",
          },
          icon: "success",
          title: "Success",
          text: response.data.message,
        });
        getInstallationPartners();
        onClose();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || "Something went wrong",
      });
    } finally {
      setAssigning(false);
    }
  };

  // when we assign installation partners to vendor
  const handleAssignInstallationPartners = async () => {
    try {
      setAssigning(true);

      if (selectedIps.length === 0) {
        return;
      }

      const token = localStorage.getItem("token");

      const payload = {
        vendorId: vendorId?._id,

        installationPartnerIds: selectedIps,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/api/assignInstallationPartners`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        Swal.fire({
          customClass: {
            container: "my-swal",
          },
          icon: "success",
          title: "Success",
          text: response.data.message,
        }).then(() => {
          onClose();
        });

        getVendorList();
      }
    } catch (error) {
      console.error(error);

      Swal.fire({
        customClass: {
          container: "my-swal",
        },
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || "Something went wrong",
      });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography fontWeight={600}>
          Assign {vpList ? "Vendor" : "Installation"} Partner
        </Typography>
      </DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder={`Search ${vpList ? "Vendor" : "Installation"} Partner`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* ---------------- LOADING ---------------- */}
        {loadingList ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : filteredIp.length === 0 ? (
          <Typography align="center" color="text.secondary">
            No {vpList ? "Vendor" : "Installation Partner"} found
          </Typography>
        ) : (
          filteredIp.map((ip) => {
            const isSelected = vpList
              ? selectedVp == ip.id
              : selectedIps.includes(ip.id);

            return (
              <MenuItem
                key={ip.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  backgroundColor: isSelected ? "#E3F2FD" : "#fff",
                }}
              >
                <div className="flex justify-between items-center  w-full ">
                  <div className="flex items-center">
                    <div className="w-16 h-16 rounded-full border border-1 overflow-hidden mr-2">
                      {ip.profilePhoto ? (
                        <img
                          src={ip?.profilePhoto}
                          alt="profilePic"
                          className=" w-full h-full"
                        />
                      ) : (
                        <img
                          src="/assests/AdminDashboard/userForIp.webp"
                          alt="ProfilePhoto"
                        />
                      )}
                    </div>
                    <div className=" flex flex-col items-start justify-between">
                      {CapitalizeFirstLetter(ip.name || ip.vendorBusinessName)}

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#555",
                          fontSize: "0.85rem",
                        }}
                      >
                        {ip?.phoneNumber}
                      </Typography>
                    </div>
                  </div>
                  <Switch
                    checked={isSelected}
                    onChange={() => {
                      if (vpList) {
                        handleVPList(ip.id);
                      } else {
                        handleToggle(ip.id);
                      }
                    }}
                    color="primary"
                  />
                </div>

                {/* <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={500}>{ip.name}</Typography>
                </Box> */}
              </MenuItem>
            );
          })
        )}
      </DialogContent>

      <DialogActions>
        <div className="w-30">
          <GlobalButton variant="secondary" size="medium" onClick={onClose}>
            {`Cancel`}
          </GlobalButton>
        </div>
        <div className="w-30">
          <GlobalButton
            variant="primary"
            size="medium"
            onClick={
              vpList
                ? handleAssignVP
                : vendorId == null
                  ? handleAssign
                  : handleAssignInstallationPartners
            }
            disabled={assigning}
            isLoading={assigning}
          >
            {`Assign`}
          </GlobalButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default IPAssignModal;
